import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Palette, Plus, Edit, Trash2, X, RotateCcw } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ColoursPage() {
  const navigate = useNavigate();
  const [colours, setColours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingColour, setEditingColour] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '#000000'
  });

  useEffect(() => {
    checkAuth();
    fetchColours();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchColours = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/colours`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch colours');
      }

      setColours(response.data.colours || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('code', formData.code);

      const method = editingColour ? 'PUT' : 'POST';
      const url = editingColour 
        ? `${BACKEND_URL}/api/admin/colours/${editingColour.id}`
        : `${BACKEND_URL}/api/admin/colours`;

      const xhr = new XMLHttpRequest();
      
      await new Promise((resolve, reject) => {
        xhr.open(method, url);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText);
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || 'Failed to save colour'));
            } catch {
              reject(new Error('Failed to save colour'));
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error occurred'));
        };
        
        xhr.send(formDataToSend);
      });

      setShowModal(false);
      setEditingColour(null);
      resetForm();
      fetchColours();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this colour?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/colours/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate colour');
      }

      fetchColours();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestore = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/colours/${id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore colour');
      }

      fetchColours();
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal = (colour = null) => {
    if (colour) {
      setEditingColour(colour);
      setFormData({
        name: colour.name,
        code: colour.code
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '#000000'
    });
    setEditingColour(null);
    setError('');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading colours...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
              <Palette className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-navy">Colours</h1>
              <p className="text-gray-600">Manage available card colours</p>
            </div>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-navy text-white hover:bg-navy/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Colour
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {colours.length > 0 ? (
          <>
            {/* Active Colours */}
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#7D64FA' }}>
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Active Colours ({colours.filter(c => c.is_active !== false).length})
              </h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                {colours.filter(c => c.is_active !== false).map((colour) => (
                  <div key={colour.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div 
                          className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm"
                          style={{ backgroundColor: colour.code }}
                        ></div>
                        <div>
                          <h3 className="font-bold text-navy">{colour.name}</h3>
                          <p className="text-xs font-mono text-gray-500">{colour.code}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openModal(colour)}
                          className="text-navy hover:text-navy/80 p-1"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(colour.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                      Active
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Inactive Colours */}
            {colours.filter(c => c.is_active === false).length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-600 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  Inactive Colours ({colours.filter(c => c.is_active === false).length})
                </h2>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 opacity-75">
                  {colours.filter(c => c.is_active === false).map((colour) => (
                    <div key={colour.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div 
                            className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm opacity-50"
                            style={{ backgroundColor: colour.code }}
                          ></div>
                          <div>
                            <h3 className="font-bold text-gray-500">{colour.name}</h3>
                            <p className="text-xs font-mono text-gray-400">{colour.code}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRestore(colour.id)}
                          className="text-green-600 hover:text-green-800 p-1 flex items-center gap-1"
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span className="text-xs">Restore</span>
                        </button>
                      </div>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                        Inactive
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <Palette className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Colours Yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first colour to get started
            </p>
            <Button
              onClick={() => openModal()}
              className="bg-navy text-white hover:bg-navy/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Colour
            </Button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-navy">
                  {editingColour ? 'Edit' : 'Add'} Colour
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Colour Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="e.g., Midnight Black"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Colour Code *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-20 h-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent font-mono"
                      placeholder="#000000"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-navy text-white hover:bg-navy/90"
                  >
                    {submitting ? 'Saving...' : editingColour ? 'Update Colour' : 'Create Colour'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
